import {
  Grid,
  createStyles,
  withStyles,
  Button,
  InputField,
  MenuItem,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import { KeypadInputField, withDialogStackContext } from '@paintscout/ui-business'
import type { WithDialogStackContext } from '@paintscout/ui-business'

import type { OptionsDocument, QuoteDocument } from 'paintscout'
import type { QuoteDetails } from '@paintscout/util/builder'
import {
  getDetails,
  getObjectLabels,
  setDetails,
  getFeatures,
  setQuoteOptions,
  getQuoteOptions
} from '@paintscout/util/builder'
import get from 'lodash/get'

import React from 'react'

export interface AdjustTotalDialogProps extends DialogProps, WithDialogStackContext {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  options: OptionsDocument
  quote: QuoteDocument
  onConfirm?: (quote: QuoteDocument) => void
  onCancel?: () => void
}

interface AdjustTotalDialogState {
  options: OptionsDocument
  quote: QuoteDocument
  isDirty: boolean
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    discountField: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      width: '100%',
      '& button': {
        marginBottom: 10,
        marginLeft: 10
      }
    },
    adornmentSelect: {
      paddingRight: theme.spacing(2)
    },
    disabledInputField: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P94
    },
    typeSelect: {
      '& $select': {
        borderLeft: 'none'
      }
    },
    visibleToggle: {
      display: 'grid',
      alignItems: 'center',
      gridTemplateColumns: 'auto auto',
      gridGap: theme.spacing()
    }
  })
}

class AdjustTotalDialog extends React.PureComponent<AdjustTotalDialogProps, AdjustTotalDialogState> {
  constructor(props: AdjustTotalDialogProps) {
    super(props)

    const { quote, options } = this.props

    this.state = {
      options,
      quote: { ...quote },
      isDirty: false
    }
  }

  public render() {
    const {
      loading,
      classes,
      quote: _quote,
      options: _options,
      dialogStackContext: _dialogStackContext,
      onConfirm: _onConfirm,
      ...otherProps
    } = this.props
    const leftButton = (
      <Button disabled={loading} onClick={this.props.onCancel} variant="textNoPadding">
        Cancel
      </Button>
    )

    return (
      <Dialog className={classes.root} {...otherProps} fullWidth={true} maxWidth={'md'}>
        <DialogTitle loading={loading}>{`Edit Sub Total`}</DialogTitle>
        <DialogContent>{this.renderContent()}</DialogContent>
        <DialogActions showBorder={true} leftButton={leftButton}>
          <Button disabled={loading} onClick={this.handleConfirm} variant={'contained'}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  public renderContent() {
    const { options, classes } = this.props
    const { currency } = getObjectLabels({ options })
    const { quote } = this.state

    const features = getFeatures({ options })
    const materialCostFeature = get(features, 'quotes.materialCost.enabled')

    const details = getDetails({ quote, options })

    const afterAdjustment = details.totals.afterAdjustment

    return (
      <>
        {materialCostFeature && (
          <>
            <Grid container={true} spacing={1} direction={'row'}>
              <Grid item={true} xs={12} sm={6}>
                <InputField
                  format={'price'}
                  classes={{ inputRoot: classes.disabledInputField }}
                  value={details.totals.labour}
                  readOnly={true}
                  label={`Labor (${currency.symbol})`}
                  fullWidth={true}
                  helperText={' '}
                />
              </Grid>
            </Grid>
            <Grid container={true} spacing={1} direction={'row'}>
              <Grid item={true} xs={12} sm={6}>
                <InputField
                  format={'price'}
                  classes={{ inputRoot: classes.disabledInputField }}
                  value={details.totals.materials}
                  readOnly={true}
                  label={`Material Cost (${currency.symbol})`}
                  fullWidth={true}
                  helperText={' '}
                />
              </Grid>
            </Grid>
          </>
        )}

        <Grid container={true} spacing={3} direction={'row'} alignItems={'center'}>
          <Grid item={true} xs={12} sm={6}>
            <InputField
              format={'price'}
              classes={{ inputRoot: classes.disabledInputField }}
              value={details.totals.price}
              readOnly={true}
              helperText={' '}
              label={`Calculated Sub Total (${currency.symbol})`}
              fullWidth={true}
            />
          </Grid>
        </Grid>
        <Grid container={true} spacing={1} direction={'row'}>
          <Grid item={true} xs={12} sm={6}>
            <div className={classes.discountField}>
              <KeypadInputField
                value={details.totalAdjustment.rate !== undefined ? details.totalAdjustment.rate : 0}
                fullWidth={true}
                margin="dense"
                format={'price'}
                label={`Adjustment (${details.totalAdjustment.type === 'percentage' ? '%' : `${currency.symbol}`})`}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  this.updateAdjustment('rate', event.target.value)
                }}
                style={{ width: '100%' }}
                endAdornment={
                  <Select
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                    }}
                    value={details.totalAdjustment.type === 'percentage' ? 'percentage' : 'value'}
                    classes={{ select: classes.adornmentSelect }}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                      this.updateAdjustment('type', event.target.value)
                    }}
                    renderValue={(value: string) => {
                      return <span>{value === 'percentage' ? `%` : `${currency.symbol}`}</span>
                    }}
                  >
                    <MenuItem value="percentage">%</MenuItem>
                    <MenuItem value="value">{`${currency.symbol}`}</MenuItem>
                  </Select>
                }
                autoSelect={true}
              />
              <Button variant="text" onClick={this.resetAdjustment}>
                Reset
              </Button>
            </div>
          </Grid>
        </Grid>
        <Grid container={true} spacing={1} direction={'row'}>
          <Grid item={true} xs={12} sm={6}>
            <div className={classes.discountField}>
              <KeypadInputField
                value={afterAdjustment}
                fullWidth={true}
                margin="dense"
                format={'price'}
                label={`Adjusted Sub Total (${currency.symbol})`}
                onChange={this.updateAdjustedTotal}
                autoSelect={true}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container={true} spacing={1} direction={'row'}>
          <Grid item={true} xs={12} sm={6}>
            <InputField
              value={details.totalAdjustment.description}
              label={'Description / Reason'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                this.updateAdjustment('description', event.target.value)
              }}
              fullWidth={true}
              margin="dense"
              multiline={true}
            />
          </Grid>
        </Grid>
      </>
    )
  }

  public updateQuoteDetails = (details: QuoteDetails) => {
    const { quote, options } = this.state

    const updatedQuote = setDetails({ quote, options, details })
    this.setState({ quote: updatedQuote, isDirty: true })
  }

  public handleOptionChange = (ev: any, value: boolean) => {
    const { quote } = this.state
    const quoteOptions = {
      ...getQuoteOptions({ quote }),
      [ev.currentTarget.name]: value
    }

    const updatedQuote = setQuoteOptions({ quote, quoteOptions })

    this.setState({
      isDirty: true,
      quote: updatedQuote
    })
  }

  public updateAdjustment = (key: keyof QuoteDetails['totalAdjustment'], value: string) => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })
    const { options } = this.state
    const updatedDetails = {
      ...details,
      totalAdjustment: {
        ...details.totalAdjustment,
        [key]: value
      }
    }

    const updatedQuote = setDetails({ quote: this.state.quote, options, details: updatedDetails })

    this.setState({ quote: updatedQuote, isDirty: true })
  }

  public updateAdjustedTotal = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (value === '') {
      return
    }

    const adjustedTotal = parseFloat(value)

    const details = getDetails({ quote: this.state.quote, options: this.state.options })

    const rate = adjustedTotal - details.totals.price

    const updatedDetails = {
      ...details,
      totalAdjustment: {
        ...details.totalAdjustment,
        type: 'value',
        rate
      }
    }

    this.updateQuoteDetails(updatedDetails)
  }

  public resetAdjustment = () => {
    const details = getDetails({ quote: this.state.quote, options: this.state.options })

    this.updateQuoteDetails({
      ...details,
      totalAdjustment: {
        ...details.totalAdjustment,
        rate: 0
      }
    })
  }

  public handleChange = (updatedQuote: QuoteDocument) => {
    this.setState({ quote: updatedQuote })
  }

  public handleConfirm = () => {
    if (this.props.onConfirm) {
      this.props.onConfirm(this.state.quote)
    }
  }
}

export default withStyles(styles)(withDialogStackContext(AdjustTotalDialog))
